import { useEffect, useRef, useState } from 'react';
import { MUSIC_TRACKS } from '../config/music';

type MusicType = 'menu' | 'battle';

interface UseMusicOptions {
  type: MusicType;
  volume?: number;
  enabled?: boolean;
}

// Глобальный менеджер музыки (singleton)
class MusicManager {
  private static instance: MusicManager | null = null;
  private audioRef: HTMLAudioElement | null = null;
  private currentType: MusicType | null = null;
  private currentTrackIndex: number = 0;
  private tracks: string[] = [];
  private isPlaying: boolean = false;
  private volume: number = 0.5;
  private enabled: boolean = true;
  private listeners: Set<() => void> = new Set();

  private constructor() { }

  static getInstance(): MusicManager {
    if (!MusicManager.instance) {
      MusicManager.instance = new MusicManager();
    }
    return MusicManager.instance;
  }

  // Останавливаем текущий трек
  private stop() {
    if (this.audioRef) {
      this.audioRef.pause();
      this.audioRef.src = '';
      this.audioRef = null;
    }
    this.isPlaying = false;
  }

  // Загружаем список треков
  private async loadTracks(type: MusicType): Promise<string[]> {
    try {
      const tracks = MUSIC_TRACKS[type];
      if (Array.isArray(tracks) && tracks.length > 0) {
        return tracks;
      }
    } catch (error) {
      console.warn(`Failed to load tracks for ${type}`);
    }
    return [];
  }

  // Проигрываем трек
  private async playTrack(type: MusicType, trackIndex: number): Promise<void> {
    if (trackIndex >= this.tracks.length) {
      // Если дошли до конца списка, начинаем сначала
      this.currentTrackIndex = 0;
      this.playTrack(type, 0);
      return;
    }

    const trackName = this.tracks[trackIndex];
    const trackPath = `./music/${type}/${trackName}`;

    // Создаем новый аудио элемент, если его нет
    if (!this.audioRef) {
      this.audioRef = new Audio();
      this.audioRef.volume = this.volume;
      this.audioRef.loop = false;

      // Когда трек заканчивается, переключаемся на следующий
      this.audioRef.addEventListener('ended', () => {
        if (this.isPlaying && this.enabled && this.tracks.length > 0 && this.currentType === type) {
          const nextIndex = (this.currentTrackIndex + 1) % this.tracks.length;
          this.currentTrackIndex = nextIndex;
          this.playTrack(type, nextIndex);
        }
      });

      // Обработка ошибок загрузки
      this.audioRef.addEventListener('error', () => {
        // Пробуем следующий трек только если музыка включена
        if (this.enabled && this.tracks.length > 0 && this.currentType === type) {
          const nextIndex = (this.currentTrackIndex + 1) % this.tracks.length;
          if (nextIndex !== this.currentTrackIndex) {
            this.currentTrackIndex = nextIndex;
            setTimeout(() => this.playTrack(type, nextIndex), 100);
          } else {
            this.isPlaying = false;
          }
        }
      });
    }

    this.audioRef.src = trackPath;
    this.currentTrackIndex = trackIndex;

    // Пытаемся воспроизвести
    const playPromise = this.audioRef.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          this.isPlaying = true;
        })
        .catch((error) => {
          console.log('Music autoplay blocked, will start on user interaction');
          this.isPlaying = false;

          // Пробуем запустить при первом клике пользователя
          const startOnInteraction = () => {
            if (this.audioRef && this.audioRef.paused && this.enabled && this.currentType === type) {
              this.audioRef.play()
                .then(() => {
                  this.isPlaying = true;
                })
                .catch(() => { });
            }
            document.removeEventListener('click', startOnInteraction);
            document.removeEventListener('touchstart', startOnInteraction);
          };
          document.addEventListener('click', startOnInteraction, { once: true });
          document.addEventListener('touchstart', startOnInteraction, { once: true });
        });
    }
  }

  // Устанавливаем музыку
  async setMusic(type: MusicType, volume: number = 0.5, enabled: boolean = true) {
    this.volume = volume;
    this.enabled = enabled;

    // Если тип музыки изменился, останавливаем текущий трек
    if (this.currentType !== null && this.currentType !== type) {
      this.stop();
      this.currentTrackIndex = 0;
    }

    this.currentType = type;

    if (!enabled) {
      this.stop();
      return;
    }

    // Загружаем треки для нового типа
    this.tracks = await this.loadTracks(type);

    if (this.tracks.length === 0) {
      this.stop();
      return;
    }

    // Обновляем громкость существующего аудио
    if (this.audioRef) {
      this.audioRef.volume = volume;
    }

    // Если тип изменился или треки еще не загружены, начинаем с первого трека
    if (this.currentType !== type || !this.audioRef || this.audioRef.paused) {
      this.currentTrackIndex = 0;
      this.playTrack(type, 0);
    }
  }

  // Обновляем громкость
  setVolume(volume: number) {
    this.volume = volume;
    if (this.audioRef) {
      this.audioRef.volume = volume;
    }
  }

  // Останавливаем музыку
  stopMusic() {
    this.stop();
    this.currentType = null;
    this.currentTrackIndex = 0;
    this.tracks = [];
  }
}

/**
 * Хук для управления фоновой музыкой
 * Автоматически проигрывает треки из папки по кругу
 * 
 * Треки загружаются из файла tracks.json в соответствующей папке
 * Формат tracks.json: ["track1.mp3", "track2.mp3", "track3.mp3"]
 * 
 * Треки должны быть размещены в:
 * - ./music/menu/ - для меню, карты и зданий
 * - ./music/battle/ - для боя
 * 
 * ВАЖНО: Все компоненты используют один глобальный экземпляр музыки,
 * поэтому при переключении между страницами старый трек автоматически останавливается.
 */
export const useMusic = ({ type, volume = 0.5, enabled = true }: UseMusicOptions) => {
  const managerRef = useRef<MusicManager | null>(null);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!managerRef.current) {
      managerRef.current = MusicManager.getInstance();
    }

    const manager = managerRef.current;
    manager.setMusic(type, volume, enabled);

    return () => {
      // При размонтировании компонента не останавливаем музыку,
      // так как другой компонент может использовать тот же тип музыки
      // Музыка остановится только если новый компонент запросит другой тип
    };
  }, [type, volume, enabled]);

  // Обновляем громкость при изменении
  useEffect(() => {
    if (managerRef.current) {
      managerRef.current.setVolume(volume);
    }
  }, [volume]);
};
